import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { proxySetIdState, rebuildAggregatedPortfolio } from 'venn-state';
import type { AggregatedPortfolioSettings, Portfolio } from 'venn-api';
import { createAggregatedPortfolioV3, createPortfolioAsMasterV3, createPortfolioV3 } from 'venn-api';
import { getHelpCenterLinks, Icon, Label, LoadingSize, Spinner, WarningNote } from 'venn-ui-kit';
import {
  assertNotNil,
  getGlobalContext,
  isHistoricalPortfolio,
  logExceptionIntoSentry,
  recursiveScaleAssets,
  recursiveUpdatePortfolioName,
  useHasFF,
} from 'venn-utils';
import { ConditionalOverlay } from '../../conditional-overlay';
import PortfoliosContext from '../../contexts/portfolios-context';
import { Modal, ModalContent, ModalFooter, ModalHeader } from '../../modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import SliderToggle from '../../slider-toggle/SliderToggle';
import Name from './Name';
import type { NameField } from './types';
import { OwnershipContextSelector } from '../components/OwnershipContextSelector';
import { isNil, isNull, omitBy } from 'lodash';
import { UserContext } from '../../contexts';

type SaveMode = 'live' | 'snapshot';

const SAVE_MODE_OPTIONS: {
  value: SaveMode;
  label: string;
  description: string;
  unavailableWhenEdited?: string;
}[] = [
  {
    value: 'live',
    label: 'Live (read-only)',
    description: 'Remains current as constituent portfolios update',
    unavailableWhenEdited: 'Unavailable once allocations and/or structure have been changed.',
  },
  {
    value: 'snapshot',
    label: 'Snapshot (editable)',
    description: 'Point-in-time snapshot of the constituent portfolios',
  },
];

interface CreatePortfolioModalProps {
  portfolio: Portfolio;
  isPercentageMode: boolean;
  total: number | undefined;
  onClose: () => void;
  onSubmitCallback?: (newPortfolio: Portfolio | undefined) => void;
  populateName: boolean;
  isStudio?: boolean;
  /** Present only for an aggregated portfolio, which can be saved as a live or a snapshot portfolio. */
  aggregatedPortfolioSettings?: AggregatedPortfolioSettings;
  /** True when allocations or structure have been edited, which rules out saving as a live portfolio. */
  isAllocationOrStructureEdited?: boolean;
}

const CreatePortfolioModal = ({
  portfolio,
  isPercentageMode,
  total,
  onClose,
  onSubmitCallback,
  populateName,
  isStudio,
  aggregatedPortfolioSettings,
  isAllocationOrStructureEdited,
}: CreatePortfolioModalProps) => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const { defaultContext, hasPermission, profileSettings } = useContext(UserContext);
  const globalContextId = getGlobalContext(profileSettings)?.value.context.id;
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [nameField, setNameField] = useState<NameField>({
    validating: true,
    isError: false,
  });
  const { masterPortfolio } = useContext(PortfoliosContext);
  /* If the user doesn't have master, force setMaster to true, unless it's a historical portfolio */
  const hasMaster = !isNil(masterPortfolio);
  const isHistorical = isHistoricalPortfolio(portfolio);
  const [createAsMaster, setCreateAsMaster] = useState<boolean>(isHistorical ? false : !hasMaster);
  const [saveMode, setSaveMode] = useState<SaveMode>(isAllocationOrStructureEdited ? 'snapshot' : 'live');
  const hasPortfolioMaintenanceFF = useHasFF('rcm_portfolio_maintenance_ff');
  const showSaveModeSelector = hasPortfolioMaintenanceFF && !isNil(aggregatedPortfolioSettings);
  const [ownerContextId, setOwnerContextId] = useState<string | undefined>(
    createAsMaster ? globalContextId : defaultContext,
  );

  const setMasterToggle = useCallback(() => {
    // disallow user to toggle master setting if missing master portfolio or if the portfolio is historical
    if (!hasMaster || isHistoricalPortfolio(portfolio)) {
      return;
    }
    const newCreateAsMaster = !createAsMaster;
    setCreateAsMaster(newCreateAsMaster);
    setOwnerContextId(newCreateAsMaster ? globalContextId : defaultContext);
  }, [createAsMaster, defaultContext, globalContextId, hasMaster, portfolio]);

  const onSubmit = useCallback(async () => {
    const { value } = nameField;
    setSubmitting(true);

    const request = createAsMaster ? createPortfolioAsMasterV3 : createPortfolioV3;
    try {
      const createSnapshot = async () => {
        let updatedPortfolio = recursiveUpdatePortfolioName(portfolio);
        if (isPercentageMode && total !== portfolio.allocation && portfolio.allocation) {
          updatedPortfolio = recursiveScaleAssets(updatedPortfolio, assertNotNil(total) / portfolio.allocation);
        }
        return request(proxySetId, {
          ...updatedPortfolio,
          id: -1,
          name: value,
          optimizationId: undefined,
          draft: false,
          demo: false,
          master: createAsMaster,
          // A snapshot must have no ongoing link to the constituents it was copied from, so the settings the
          // aggregate was built with must not travel with it.
          aggregatedPortfolioSettings: undefined,
          ...omitBy({ ownerContextId }, isNull),
        });
      };

      const newPortfolio =
        showSaveModeSelector && saveMode === 'live'
          ? // A live aggregate comes back as a bare root carrying only its settings, so the combined tree has to be
            // rebuilt before handing it over, otherwise it renders with no constituents and a zero total.
            await rebuildAggregatedPortfolio(
              (await createAggregatedPortfolioV3(proxySetId, { name: value, aggregatedPortfolioSettings })).content,
              proxySetId,
              undefined,
            )
          : (await createSnapshot()).content;
      onSubmitCallback?.(newPortfolio);
    } catch (error) {
      const e = await error;
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      logExceptionIntoSentry(e);
      onSubmitCallback?.(undefined);
    }

    setSubmitting(false);
    onClose();
  }, [
    createAsMaster,
    isPercentageMode,
    nameField,
    onClose,
    onSubmitCallback,
    portfolio,
    total,
    ownerContextId,
    proxySetId,
    showSaveModeSelector,
    saveMode,
    aggregatedPortfolioSettings,
  ]);

  const enabled = !nameField.isError && !nameField.validating && !submitting;
  const loading = nameField.validating || submitting;

  const hasNewPermissions = useHasFF('new_permissions_ff');
  return (
    <Modal onClose={onClose}>
      <ConditionalOverlay condition={submitting}>
        <ModalHeader>Save New Portfolio</ModalHeader>
        <ModalContent>
          {isStudio && (
            <>
              All instances of <span className="font-bold">{portfolio.name}</span> in this view will be replaced by the
              newly created portfolio.
            </>
          )}
          <Name
            onChange={setNameField}
            initialValue={populateName ? (portfolio.draft ? portfolio.name : `${portfolio.name} (Copy)`) : undefined}
          />

          {showSaveModeSelector && (
            <RadioGroup className="mt-5" value={saveMode} onValueChange={(value) => setSaveMode(value as SaveMode)}>
              {SAVE_MODE_OPTIONS.map(({ value, label, description, unavailableWhenEdited }) => {
                const disabled = !!unavailableWhenEdited && !!isAllocationOrStructureEdited;
                return (
                  <label
                    key={value}
                    htmlFor={`save-mode-${value}`}
                    className={cn(
                      'flex items-start gap-[6px] font-normal',
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                  >
                    <RadioGroupItem
                      id={`save-mode-${value}`}
                      value={value}
                      disabled={disabled}
                      state={saveMode === value ? 'checked' : 'unchecked'}
                    />
                    <div className={cn(disabled && '[&>span]:text-venn-grey-60')}>
                      <span className="block text-[14px] text-venn-black">{label}</span>
                      <span className="block text-[12px] text-venn-grey-80">{description}</span>
                      {disabled && (
                        <WarningNote size="large" className="mt-[4px]">
                          {unavailableWhenEdited}
                        </WarningNote>
                      )}
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          )}

          {hasNewPermissions && (
            <OwnershipContextSelector
              disabled={createAsMaster}
              requiredAction="CREATE_PORTFOLIO"
              ownerContextId={ownerContextId}
              setOwnerContextId={setOwnerContextId}
            />
          )}
          {hasPermission('MANAGE_MASTER_PORTFOLIO') && !isHistoricalPortfolio(portfolio) && (
            <>
              <LabelContainer>
                <Label className="qa-dropmenu-input-label">
                  Set master portfolio:{' '}
                  <StyledInfoLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href={getHelpCenterLinks().MASTER_PORTFOLIO_ARTICLE_HREF}
                  >
                    <StyledInfoIcon type="info-circle" prefix="fas" />
                  </StyledInfoLink>{' '}
                </Label>
              </LabelContainer>
              <ToggleContainer>
                <SliderToggle toggled={createAsMaster} onToggle={setMasterToggle} disabled={!hasMaster} />
              </ToggleContainer>
            </>
          )}
        </ModalContent>
        <StyledFooter
          onCancel={onClose}
          primaryLabel="Save"
          primaryClassName="qa-create-portfolio"
          primaryDisabled={!enabled}
          rightChildren={loading && <Spinner size={LoadingSize.small} />}
          onPrimaryClick={onSubmit}
        />
      </ConditionalOverlay>
    </Modal>
  );
};

export default CreatePortfolioModal;

const StyledFooter = styled(ModalFooter)`
  .qa-create-portfolio {
    width: 175px;
  }
`;

const LabelContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 5px;
`;

const ToggleContainer = styled.div`
  width: 80px;
`;

const StyledInfoLink = styled.a`
  color: inherit;
`;

const StyledInfoIcon = styled(Icon)`
  margin-left: 3px;
`;
